import React, { useCallback, useMemo, useRef, useState } from 'react';
import { FileError } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { AppButton, Box, Preloader } from '@common-fe/common-fe';
import styled from 'styled-components';

import { FileImage } from '@/common/constants';
import ScanPreviewContainer from '@/components/controls/DropScan/ScanPreview/ScanPreviewContainer';
import useFilesInDocumentStore from '@/components/controls/DropScan/useFilesInDocument.store';
import ZoomRotateToolbar from '@/components/controls/DropScan/ZoomRotateToolbar/ZoomRotateToolbar';
import { PinchZoomPanInstance } from '@/components/elements/vendor/PinchZoomPan';
import { USE_MY_MONEY_UPLOAD_STORE_KEY } from '@/modules/UseMyMoney/UseMyMoney.types';

const ScrollablePreview = styled(Box)`
  position: relative;
  height: 420px;
  overflow-y: auto;
  overflow-x: hidden;
  border-radius: 8px;

  [data-testid='zoom-button'] {
    visibility: hidden;
    pointer-events: none;
  }

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.15);
    border-radius: 3px;
  }
`;

const PreviewInner = styled(Box)`
  min-height: 100%;
  display: flex;
  flex-direction: column;
`;

const ToolbarWrapper = styled.div`
  position: sticky;
  bottom: 16px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  z-index: 2;
  pointer-events: none;

  > * {
    pointer-events: all;
  }
`;

interface Props {
  onScan: () => void;
  onBack: () => void;
  onSetFiles: (files: FileImage[]) => void;
  onDeleteFile: (fileId: string) => void;
  onFileUploadError: (error?: FileError) => void;
  isLoading?: boolean;
  isMobile?: boolean;
  overrideFiles?: FileImage[];
}

const PreviewFile: React.FC<Props> = ({
  onScan,
  onBack,
  onSetFiles,
  onDeleteFile,
  onFileUploadError,
  isLoading,
  isMobile,
  overrideFiles,
}) => {
  const { t } = useTranslation();

  const previewRef = useRef<HTMLDivElement>(null);
  const pinchZoomRef = useRef<PinchZoomPanInstance>(null);
  const [rotation, setRotation] = useState(0);
  const [currentScale, setCurrentScale] = useState(1);

  const INITIAL_SCALE = 1;
  const ZOOM_LEVELS = [INITIAL_SCALE / 1.75, INITIAL_SCALE, INITIAL_SCALE * 1.75, INITIAL_SCALE * 3.0];
  const isZoomInDisabled = currentScale >= ZOOM_LEVELS[ZOOM_LEVELS.length - 1] - 0.001;
  const isZoomOutDisabled = currentScale <= ZOOM_LEVELS[0] + 0.001;

  const { values } = useFilesInDocumentStore(USE_MY_MONEY_UPLOAD_STORE_KEY);
  const allFiles = useMemo(
    () => overrideFiles ?? (values?.files?.length ? values?.files : []),
    [overrideFiles, values?.files]
  );

  const handleZoomIn = useCallback(() => {
    if (pinchZoomRef.current) {
      pinchZoomRef.current.handleZoomInClick();
    } else {
      const zoomWrapper = previewRef.current?.querySelector<HTMLElement>('[data-testid="zoom-button"]');
      const btn = zoomWrapper?.querySelector<HTMLButtonElement>('button:last-child');
      btn?.click();
    }
  }, []);

  const handleZoomOut = useCallback(() => {
    if (pinchZoomRef.current) {
      pinchZoomRef.current.handleZoomOutClick();
    } else {
      const zoomWrapper = previewRef.current?.querySelector<HTMLElement>('[data-testid="zoom-button"]');
      const btn = zoomWrapper?.querySelector<HTMLButtonElement>('button:first-child');
      btn?.click();
    }
  }, []);

  const handleRotate = useCallback(() => {
    setRotation((prev) => prev + 90);
  }, []);

  const hasFiles = allFiles.length > 0;

  return (
    <Box data-testId="preview-file_wrapper" {...(isMobile ? {} : { flex: 'grow' })}>
      <Box pad="spacing12" round="container1Round" background="module" flex="grow">
        <ScrollablePreview ref={previewRef} flex="grow">
          <PreviewInner flex="grow">
            <ScanPreviewContainer
              isShowTooltip={false}
              isActiveFileShown
              storeKey={USE_MY_MONEY_UPLOAD_STORE_KEY}
              defaultFiles={allFiles}
              handleDeleteFile={onDeleteFile}
              onSetFiles={onSetFiles}
              onFileUploadError={onFileUploadError}
              ocrRequired
              overrideFiles={overrideFiles}
              rotation={rotation}
              isThumbsTopPosition
              pinchZoomRef={pinchZoomRef}
              hideShadow
              onScaleChange={setCurrentScale}
            />
          </PreviewInner>

          {hasFiles && (
            <ToolbarWrapper>
              <ZoomRotateToolbar
                floating={false}
                onZoomIn={handleZoomIn}
                onZoomOut={handleZoomOut}
                onRotate={handleRotate}
                isZoomInDisabled={isZoomInDisabled}
                isZoomOutDisabled={isZoomOutDisabled}
              />
            </ToolbarWrapper>
          )}
        </ScrollablePreview>
      </Box>

      <Box margin={{ top: 'spacing24' }} direction="row" gap="spacing12" justify="end">
        <Box width={{ max: isMobile ? '50%' : '160px', width: '100%' }}>
          <AppButton testId="preview-file-back" width="100%" buttonType="secondary" onClick={onBack}>
            {t('Back')}
          </AppButton>
        </Box>
        <Box width={{ max: isMobile ? '50%' : '160px', width: '100%' }}>
          <AppButton testId="preview-file-confirm" width="100%" onClick={onScan} disabled={isLoading}>
            {isLoading ? <Preloader color="white" testId="preview-file-confirm_loader" /> : t('Confirm')}
          </AppButton>
        </Box>
      </Box>
    </Box>
  );
};

export default PreviewFile;
